import { reactive, computed, App } from 'vue';

// 路由配置接口
export interface RouteConfig {
  path: string;
  name?: string;
  component: any;
  props?: boolean;
  meta?: Record<string, any>;
}

// 当前路由状态接口
export interface RouteLocation {
  path: string;
  name?: string;
  query: Record<string, string>;
  params: Record<string, any>;
  meta?: Record<string, any>;
}

class CustomRouter {
  private routes: RouteConfig[] = [];
  private currentRoute = reactive<RouteLocation>({
    path: '/',
    query: {},
    params: {},
    meta: {},
  });

  constructor(routes: RouteConfig[]) {
    this.routes = routes;
    this.init();
  }

  // 获取当前路由（响应式）
  get route() {
    return computed(() => this.currentRoute);
  }

  // 获取当前组件
  get currentComponent() {
    return computed(() => {
      const route = this.routes.find(r => r.path === this.currentRoute.path);
      return route?.component || this.routes[0]?.component;
    });
  }

  // 编程式导航
  push(to: string | { path: string; query?: Record<string, any> }) {
    let url = '';
    
    if (typeof to === 'string') {
      url = to;
    } else {
      url = to.path;
      if (to.query && Object.keys(to.query).length > 0) {
        const queryString = new URLSearchParams(to.query).toString();
        url += `?${queryString}`;
      }
    }

    // 使用 pushState 改变 URL 但不刷新页面
    window.history.pushState({}, '', url);
    this.updateRoute();
  }

  // 替换当前路由
  replace(to: string | { path: string; query?: Record<string, any> }) {
    let url = '';
    
    if (typeof to === 'string') {
      url = to;
    } else {
      url = to.path;
      if (to.query && Object.keys(to.query).length > 0) {
        const queryString = new URLSearchParams(to.query).toString();
        url += `?${queryString}`;
      }
    }

    window.history.replaceState({}, '', url);
    this.updateRoute();
  }

  // 后退
  back() {
    window.history.back();
  }

  // 前进
  forward() {
    window.history.forward();
  }

  // 安装插件
  install(app: App) {
    // 提供全局路由实例
    app.config.globalProperties.$router = this;
    app.config.globalProperties.$route = this.route;
    
    // 提供注入
    app.provide('router', this);
    app.provide('route', this.route);
  }


  // 初始化路由器
  private init() {
    // 监听 popstate 事件（浏览器前进/后退）
    window.addEventListener('popstate', () => {
      this.updateRoute();
    });

    // 初始化当前路由
    this.updateRoute();
  }

  // 解析 URL 获取路径和查询参数
  private parseUrl(url: string = window.location.pathname + window.location.search) {
    const [pathname, search] = url.split('?');
    const query: Record<string, string> = {};

    if (search) {
      const params = new URLSearchParams(search);
      params.forEach((value, key) => {
        query[key] = value;
      });
    }

    return { pathname, query };
  }

  // 更新当前路由状态
  private updateRoute() {
    const { pathname, query } = this.parseUrl();
    
    // 查找匹配的路由
    const route = this.routes.find(r => r.path === pathname) || this.routes[0];

    this.currentRoute.path = pathname;
    this.currentRoute.name = route?.name;
    this.currentRoute.query = query;
    this.currentRoute.params = route?.props ? query : {};
    this.currentRoute.meta = route?.meta || {};

    // 更新页面标题
    if (route?.meta?.title) {
      document.title = route.meta.title;
    }
  }
}

// 创建路由器实例
export function createCustomRouter(routes: RouteConfig[]) {
  return new CustomRouter(routes);
}

export default CustomRouter;
